<script lang="ts" module>
	export interface CartLineItem {
		type: CompetitionCategoryType;
		price: number;
	}
	export interface CartDog {
		id: string;
		index: number;
		callName?: string;
		items: CartLineItem[];
		subtotal: number;
	}
</script>

<script lang="ts">
	import { getTranslate } from "@utils/translate.js";
	import type { CompetitionCategoryType } from "@core/schema/CompetitionCategoryType.js";
	import type { Locale } from "@core/schema/Locale.js";
	import { competitionCategoryTypeNameMap } from "@core/schema/names/competitionCategoryTypeNameMap.js";
	import { formatEur } from "@core/utils/formatEur.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import translations from "./CartSummary.translations.json" with { type: "json" };

	const {
		breakdown,
		totalCzk,
		eurRate,
		locale,
		sending,
		submitLabel,
		sendingLabel,
		compact = false,
	}: {
		breakdown: CartDog[];
		totalCzk: number;
		eurRate: number | undefined;
		locale: Locale;
		sending: boolean;
		submitLabel: string;
		sendingLabel: string;
		compact?: boolean;
	} = $props();

	const translate = getTranslate(translations);

	const dogsWithItems = $derived(breakdown.filter((dog) => dog.items.length > 0));
	const totalEur = $derived(formatEur(totalCzk, eurRate, locale));

	function priceLabel(price: number): string {
		return formatPrice(price, locale, false);
	}
	function dogLabel(dog: CartDog): string {
		return dog.callName?.trim() ? dog.callName : `${translate("dog")} #${dog.index + 1}`;
	}

	const submitClass =
		"ease-spring focus-visible:ring-secondary/45 mt-1 flex w-full items-center justify-center gap-2 rounded-md " +
		"bg-secondary px-6 py-3.5 font-semibold tracking-[0.02em] text-white shadow-action transition duration-150 " +
		"hover:-translate-y-px hover:bg-secondary-600 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 " +
		"disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60";

	const arrowIcon =
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
		'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';

	/* eslint-disable svelte/no-at-html-tags */
	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet totalRow()}
	<!-- flex-wrap + ml-auto keeps the label and price on one line when they fit and
	     drops the (non-breaking) price to its own right-aligned line when the amount
	     is large — so it never wraps mid-number or overflows the narrow panel. -->
	<div class="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
		<span class="eyebrow text-ink-500">{translate("total")}</span>
		<span class="tabular text-secondary ml-auto whitespace-nowrap text-sm lg:text-md font-bold"
			>{priceLabel(totalCzk)}</span
		>
		{#if totalEur}
			<span class="tabular text-ink-500 w-full whitespace-nowrap text-right text-xs">≈ {totalEur}</span>
		{/if}
	</div>
{/snippet}

{#snippet submit()}
	<button type="submit" class={submitClass} disabled={sending}>
		{#if sending}
			{sendingLabel}
		{:else}
			{submitLabel}
			<span class="h-4 w-4">{@html arrowIcon}</span>
		{/if}
	</button>
{/snippet}

{#if compact}
	<!-- Mobile: fixed bottom action bar -->
	<div
		class="border-ink-200 bg-page/95 fixed inset-x-0 bottom-0 z-30 border-t px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-6px_24px_-8px_rgba(32,31,31,0.22)] backdrop-blur lg:hidden"
	>
		<div class="mx-auto flex max-w-3xl flex-col gap-2">
			{@render totalRow()}
			{@render submit()}
		</div>
	</div>
{:else}
	<!-- Desktop: sticky side panel -->
	<div class="border-ink-200 shadow-card rounded-xl border bg-white p-5">
		<p class="eyebrow text-secondary mb-3">{translate("summary")}</p>

		{#if dogsWithItems.length > 0}
			<ul class="divide-ink-100 flex flex-col divide-y">
				{#each dogsWithItems as dog (dog.id)}
					<li class="flex flex-col gap-1.5 py-3 first:pt-0">
						<p class="text-ink-900 text-sm font-semibold">{dogLabel(dog)}</p>
						<ul class="flex flex-col gap-1">
							{#each dog.items as item (item.type)}
								<li class="text-ink-600 flex items-baseline justify-between gap-3 text-sm">
									<span>{competitionCategoryTypeNameMap[item.type][locale]}</span>
									<span class="tabular text-ink-700 whitespace-nowrap">{priceLabel(item.price)}</span>
								</li>
							{/each}
						</ul>
					</li>
				{/each}
			</ul>
			<div class="border-ink-200 mt-3 border-t pt-3">
				{@render totalRow()}
			</div>
		{:else}
			<p class="text-ink-500 text-sm leading-relaxed">{translate("emptyHint")}</p>
		{/if}

		{@render submit()}
	</div>
{/if}
